import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Building2,
  Users,
  Send,
  Mail,
  Package,
  AlertCircle,
  Activity,
  RefreshCw,
  FileBarChart,
} from 'lucide-react';
import AdminStatsCards from './admin/AdminStatsCards';
import DceOrdersTab from './admin/DceOrdersTab';
import UsersTab from './admin/UsersTab';
import ActivityTab from './admin/ActivityTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [dceOrders, setDceOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [deliveryUrl, setDeliveryUrl] = useState('');
  const [delivering, setDelivering] = useState(null);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [recapPeriod, setRecapPeriod] = useState(1);
  const [userActivity, setUserActivity] = useState(null);
  const [activityPeriod, setActivityPeriod] = useState(30);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [purgingTests, setPurgingTests] = useState(false);

  // Purge test accounts (preview first, then confirm)
  const purgeTestAccounts = async () => {
    try {
      // Step 1: preview which accounts will be deleted
      const preview = await fetch(`${API_URL}/api/admin/users/test-accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!preview.ok) {
        alert('Impossible de récupérer la liste des comptes de test');
        return;
      }
      const data = await preview.json();
      if (!data.total) {
        alert('Aucun compte de test détecté.');
        return;
      }
      const emailList = data.users.map(u => `  • ${u.email}`).join('\n');
      const ok = window.confirm(
        `${data.total} compte(s) de test vont être supprimés DÉFINITIVEMENT (avec leurs données associées) :\n\n${emailList}\n\nConfirmer la suppression ?`
      );
      if (!ok) return;

      // Step 2: delete
      setPurgingTests(true);
      const res = await fetch(`${API_URL}/api/admin/users/test-accounts`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        alert('Erreur: ' + (err.detail || 'Suppression échouée'));
        return;
      }
      const result = await res.json();
      alert(`✅ ${result.deleted_count} compte(s) supprimé(s).`);
      await fetchUsers();
    } catch (e) {
      alert('Erreur de connexion');
    } finally {
      setPurgingTests(false);
    }
  };

  // Send recap email
  const sendRecapEmail = async () => {
    setSendingRecap(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/recap/send?period_days=${recapPeriod}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ Récapitulatif envoyé !\n\n${data.stats.new_users} inscription(s)\n${data.stats.dce_orders} commande(s) DCE\n${data.stats.revenue.toFixed(2)}€ de CA`);
      } else {
        const error = await response.json();
        alert('Erreur: ' + (error.detail || 'Échec de l\'envoi'));
      }
    } catch (err) {
      alert('Erreur de connexion');
    } finally {
      setSendingRecap(false);
    }
  };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      } else if (response.status === 403) {
        setError('Accès réservé aux administrateurs');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
    // API_URL is a stable module-scope constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch user activity
  const fetchUserActivity = useCallback(async () => {
    setLoadingActivity(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/user-activity?days=${activityPeriod}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserActivity(data);
      }
    } catch (err) {
      console.error('Error fetching user activity:', err);
    } finally {
      setLoadingActivity(false);
    }
    // API_URL is a stable module-scope constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activityPeriod]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
    // API_URL is a stable module-scope constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch DCE orders
  const fetchDceOrders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dce/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDceOrders(data.orders || []);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
    // API_URL is a stable module-scope constant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Deliver DCE order
  const deliverOrder = async (orderId) => {
    if (!deliveryUrl.trim()) {
      alert('Veuillez entrer l\'URL de téléchargement du DCE');
      return;
    }
    
    setDelivering(orderId);
    try {
      const response = await fetch(`${API_URL}/api/admin/dce/orders/${orderId}/deliver?download_url=${encodeURIComponent(deliveryUrl)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('DCE livré avec succès ! Email envoyé au client.');
        setDeliveryUrl('');
        setExpandedOrder(null);
        fetchDceOrders();
        fetchDashboard();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la livraison');
      }
    } catch (err) {
      alert('Erreur de connexion');
    } finally {
      setDelivering(null);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchUsers(), fetchDceOrders(), fetchUserActivity()]);
      setLoading(false);
    };
    
    if (token) {
      loadData();
    }
  }, [token, fetchDashboard, fetchUsers, fetchDceOrders, fetchUserActivity]);

  // Reload activity when period changes
  useEffect(() => {
    if (token && activeTab === 'activity') {
      fetchUserActivity();
    }
  }, [activityPeriod, token, activeTab, fetchUserActivity]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Accès refusé</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <Link to="/dashboard">
              <Button>Retour au dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Returns a human-friendly relative time + dormant flag.
  const formatRelativeActivity = (u) => {
    if (!u.last_activity) {
      return { label: 'Aucune activité', dormant: true };
    }
    const days = u.dormant_days;
    if (days === null || days === undefined) {
      return { label: formatDate(u.last_activity), dormant: false };
    }
    if (days === 0) return { label: "Aujourd'hui", dormant: false };
    if (days === 1) return { label: 'Hier', dormant: false };
    if (days < 7) return { label: `il y a ${days} jours`, dormant: false };
    if (days < 30) return { label: `il y a ${Math.floor(days / 7)} sem.`, dormant: false };
    if (days < 365) return { label: `il y a ${Math.floor(days / 30)} mois`, dormant: true };
    return { label: `il y a ${Math.floor(days / 365)} an(s)`, dormant: true };
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus !== 'paid') {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Non payé</span>;
    }
    switch (status) {
      case 'delivered':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Livré</span>;
      case 'processing':
        return <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">À livrer</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">{status}</span>;
    }
  };

  // Send profile-completion reminder to subscribers without a company profile
  const sendProfileCompletionReminders = async () => {
    try {
      const preview = await fetch(`${API_URL}/api/newsletter/profile-completion/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!preview.ok) {
        alert('Impossible de récupérer les abonnés sans profil');
        return;
      }
      const data = await preview.json();
      if (!data.will_send) {
        alert(
          data.candidates === 0
            ? 'Aucun abonné newsletter sans profil entreprise — rien à envoyer.'
            : `Tous les ${data.candidates} candidat(s) ont déjà reçu la relance.`
        );
        return;
      }
      const list = data.users
        .filter(u => u.will_send)
        .map(u => `  • ${u.email} (${u.company_name || 'société non renseignée'})`)
        .join('\n');
      const ok = window.confirm(
        `Un email "Complétez votre profil" sera envoyé à ${data.will_send} abonné(s) :\n\n${list}\n\nEnvoyer ?`
      );
      if (!ok) return;

      const res = await fetch(`${API_URL}/api/newsletter/send-profile-completion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        alert('Erreur: ' + (err.detail || 'Envoi échoué'));
        return;
      }
      const result = await res.json();
      const r = result.results || {};
      alert(`✅ Envoyés: ${r.sent || 0}\nSkipés: ${r.skipped || 0}\nErreurs: ${(r.errors || []).length}`);
    } catch (e) {
      alert('Erreur de connexion');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
            <span className="ml-2 px-2 py-1 bg-amber-500 text-xs rounded font-bold">ADMIN</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-white hover:bg-white/10">Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <AdminStatsCards dashboard={dashboard} />

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={activeTab === 'dashboard' ? 'default' : 'outline'}
            onClick={() => setActiveTab('dashboard')}
          >
            <Package className="w-4 h-4 mr-2" />
            Commandes DCE
          </Button>
          <Button
            variant={activeTab === 'users' ? 'default' : 'outline'}
            onClick={() => setActiveTab('users')}
          >
            <Users className="w-4 h-4 mr-2" />
            Utilisateurs ({users.length})
          </Button>
          <Button
            variant={activeTab === 'activity' ? 'default' : 'outline'}
            onClick={() => setActiveTab('activity')}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 hover:from-emerald-600 hover:to-teal-600"
          >
            <Activity className="w-4 h-4 mr-2" />
            Activité
          </Button>
          
          {/* Recap Email Section */}
          <div className="ml-auto flex items-center gap-2">
            <select 
              value={recapPeriod}
              onChange={(e) => setRecapPeriod(parseInt(e.target.value))}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
            >
              <option value={1}>Aujourd&apos;hui</option>
              <option value={7}>7 derniers jours</option>
              <option value={30}>30 derniers jours</option>
            </select>
            <Button
              variant="outline"
              onClick={sendRecapEmail}
              disabled={sendingRecap}
              className="bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100"
            >
              {sendingRecap ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileBarChart className="w-4 h-4 mr-2" />
              )}
              Envoyer récap
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                fetchDashboard();
                fetchUsers();
                fetchDceOrders();
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* DCE Orders Tab */}
        {activeTab === 'dashboard' && (
          <DceOrdersTab
            dceOrders={dceOrders}
            expandedOrder={expandedOrder}
            setExpandedOrder={setExpandedOrder}
            deliveryUrl={deliveryUrl}
            setDeliveryUrl={setDeliveryUrl}
            delivering={delivering}
            deliverOrder={deliverOrder}
            getStatusBadge={getStatusBadge}
            formatDate={formatDate}
          />
        )}
        {/* Users Tab */}
        {activeTab === 'users' && (
          <UsersTab
            users={users}
            purgingTests={purgingTests}
            purgeTestAccounts={purgeTestAccounts}
            sendProfileCompletionReminders={sendProfileCompletionReminders}
            formatDate={formatDate}
            formatRelativeActivity={formatRelativeActivity}
          />
        )}
        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <ActivityTab
            userActivity={userActivity}
            loadingActivity={loadingActivity}
            activityPeriod={activityPeriod}
            setActivityPeriod={setActivityPeriod}
            fetchUserActivity={fetchUserActivity}
          />
        )}
      </div>
    </div>
  );
}
